import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ExchangeRate, YahooBidReserveAmountGQL, YahooBidReserveAmountQuery, YahooBidReserveAmountQueryVariables } from '../../../../../../../graphql/user-service';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, finalize, map, Observable, skip, Subject, switchMap, takeUntil } from 'rxjs';
import { QueryHandlingService, WatchQueryRef } from '@akebono/core';

@Component({
  selector: 'app-auction-reserve-amount',
  templateUrl: './auction-reserve-amount.component.html',
  styleUrls: ['./auction-reserve-amount.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AuctionReserveAmountComponent implements OnInit, OnDestroy {
  @Input() auctionId!: string;
  @Input() exchangeRate!: ExchangeRate;

  private amount$ = new BehaviorSubject<number | null>(null);
  @Input() set amount(value: number) {
    if (value) {
      this.amount$.next(value);
    }
  }

  private quantity$ = new BehaviorSubject<number>(1);
  @Input() set quantity(value: number) {
    if (value) {
      this.quantity$.next(value);
    }
  }

  private loadingSubject = new BehaviorSubject(false);
  loading$ = this.loadingSubject.asObservable();

  private queryRef!: WatchQueryRef<YahooBidReserveAmountQuery, YahooBidReserveAmountQueryVariables>;
  reserveAmount$!: Observable<number | null>;

  private destroy$ = new Subject<void>();

  constructor(
    private qhs: QueryHandlingService,
    private yahooBidReserveAmountGQL: YahooBidReserveAmountGQL,
  ) {}

  ngOnInit(): void {
    this.queryRef = this.qhs.watch<
      YahooBidReserveAmountQuery,
      YahooBidReserveAmountQueryVariables
    >(
      this.yahooBidReserveAmountGQL,
      {
        auctionId: this.auctionId,
        amount: this.amount$.value ?? 0,
        quantity: this.quantity$.value,
      },
      'cache-first',
    );

    this.reserveAmount$ = this.queryRef.data.pipe(
      map(data => data?.yahooBidReserveAmount ?? null),
    );

    combineLatest([this.amount$, this.quantity$])
      .pipe(
        skip(1), 
        debounceTime(300),
        distinctUntilChanged((previous, current) =>
          previous[0] === current[0] && previous[1] === current[1]
        ),
        switchMap(([amount, quantity]) => {
          this.loadingSubject.next(true);
          
          return this.queryRef.refetch({
            auctionId: this.auctionId,
            amount: amount ?? 0,
            quantity,
          }).pipe(
            finalize(() => this.loadingSubject.next(false))
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
